import React, { useState, useEffect } from "react";
import "./CabDetails.css";
import { Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { FaChevronLeft } from "react-icons/fa";
import { BsInfoCircleFill } from "react-icons/bs";
import { FaCarSide } from "react-icons/fa6";
import { SlSpeedometer } from "react-icons/sl";
import { BsFillFuelPumpFill } from "react-icons/bs";
import { FiPlusCircle } from "react-icons/fi";
import { IoMdArrowBack } from "react-icons/io";
import { MdEmail } from "react-icons/md";
import { FaPhone } from "react-icons/fa";
import DOMPurify from "dompurify";
import CabPromoCodes from "./CabPromoCodes";
import { useNavigate } from "react-router-dom";
import CabDetailsSkeleton from "./CabDetailsSkeleton";
import CabListSkeleton from "../CabListing/CabListSkeleton";
import CabReviewPage from "./CabReviewPage";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PromoCode from "../../Flight/FlightDetail/PromoCode";

const CabDetails = () => {
  const [title, setTitle] = useState("Mr.");
  const [showGST, setShowGST] = useState(false);

  const [selectedCab, setSelectedCab] = useState(null);
  const [cabDetails, setCabDetails] = useState({});

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropAddress, setDropAddress] = useState("");
  const [gstCompany, setGstCompany] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [errors, setErrors] = useState({});
  const [tripType, setTripType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    let newErrors = {};

    if (!title) newErrors.title = "Please select a title";
    if (!firstName.trim()) newErrors.firstName = "First name is required";
    else if (!/^[A-Za-z]{2,}$/.test(firstName))
      newErrors.firstName = "Enter a valid first name";

    if (!lastName.trim()) newErrors.lastName = "Last name is required";
    else if (!/^[A-Za-z]{2,}$/.test(lastName))
      newErrors.lastName = "Enter a valid last name";

    if (!email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email))
      newErrors.email = "Enter a valid email";

    if (!phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(phone))
      newErrors.phone = "Enter a valid 10-digit phone number";

    if (!pickupAddress.trim())
      newErrors.pickupAddress = "Pick-up address is required";
    else if (pickupAddress.length < 5)
      newErrors.pickupAddress = "Address is too short";

    if (!dropAddress.trim())
      newErrors.dropAddress = "Drop-off address is required";
    else if (dropAddress.length < 5)
      newErrors.dropAddress = "Address is too short";

    if (showGST) {
      if (!gstCompany.trim()) newErrors.gstCompany = "Company name is required";
      if (!gstNumber.trim()) newErrors.gstNumber = "GST number is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (validateForm()) {
  //     console.log("Form submitted:", {
  //       title, firstName, lastName, email, phone, pickupAddress, dropAddress, gstCompany, gstNumber
  //     });
  //     // Proceed with API call or navigation
  //   }
  // };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const [paymentInitializing, setPaymentInitializing] = useState(false);
  const [bookingProcessing, setBookingProcessing] = useState(false);

  const handlePayment = async () => {
    setPaymentInitializing(true); // Start payment initialization

    const res = await loadRazorpayScript();
    if (!res) {
      Swal.fire({
        icon: "error",
        title: "Razorpay SDK Failed",
        text: "Please refresh and try again.",
      });
      setPaymentInitializing(false);
      return;
    }

    try {
      setOpenBusReview(false);

      // STEP 1: Calculate final payable amount (with coupon discount if applied)
      const baseAmount = parseInt(selectedCab.price || 0);

      const finalAmount = Math.round(
        baseAmount - (selectedCoupon ? selectedCoupon.discount : 0)
      );

      // STEP 2: Create Razorpay order
      const { data: orderData } = await axios.post(
        "https://admin.travelsdata.com/api/Cab/CreateOrder",
        {
          amount: finalAmount,
        }
      );

      if (orderData.success === false) {
        Swal.fire({
          icon: "error",
          title: "Order Creation Failed",
          text: "Could not initiate payment. Please try again.",
        });
        setPaymentInitializing(false);
        return;
      }

      // STEP 3: Razorpay options
      const options = {
        key: orderData.data.key,
        amount: orderData.data.amount,
        currency: orderData.data.currency,
        name: "Innovate Blues",
        description: "Innovate Blues Cab Booking",
        image: "/Images/tripgoo.png",
        order_id: orderData.data.order_id,
        handler: async function (response) {
          try {
            // STEP 4: Verify payment
            const { data: verifyData } = await axios.post(
              "https://admin.travelsdata.com/api/Cab/VerifyPayment",
              {
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                type: "web",
              }
            );

            if (verifyData.success) {
              toast.success("✅ Payment Successful!");
              setBookingProcessing(true);
              handleSubmit();
            } else {
              Swal.fire({
                icon: "error",
                title: "Payment Verification Failed",
                text: "Please contact support.",
              });
            }
          } catch (verifyErr) {
            console.error("Verification error:", verifyErr);
            Swal.fire({
              icon: "error",
              title: "Verification Error",
              text: "An error occurred while verifying your payment.",
            });
          } finally {
            setPaymentInitializing(false);
          }
        },
        prefill: {
          name: "Innovate Blues",
          email: "support@innovateblues.com",
          contact: "+91 9718278968",
        },
        theme: { color: "#1d489f" },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setPaymentInitializing(false); // Hide loading when Razorpay modal opens
    } catch (err) {
      console.error("Order creation error:", err);
      Swal.fire({
        icon: "error",
        title: "Payment Error",
        text: "Something went wrong while creating the order.",
      });
      setPaymentInitializing(false);
    }
  };

  const navigate = useNavigate();
  const handleSubmit = async () => {
    // e.preventDefault();
    setPaymentInitializing(true);
    if (!selectedCab) {
      alert("Please select a cab before booking.");
      return;
    }

    if (validateForm()) {
      // setLoading(true); // start loader

      const payload = {
        UserEmail: email,
        UserPhone: phone,
        Type: "web",
        type: tripType,
        pickup: "New Delhi Railway Station",
        plat: "28.6434",
        plng: "77.2220",
        drop: "Indira Gandhi International Airport",
        dlat: "28.5562",
        dlng: "77.1000",
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime,
        cab_id: selectedCab.id,
        name: `${title} ${firstName} ${lastName}`,
        email: email,
        phone: Number(phone),
        phone_code: "91",
        mrp: selectedCab.mrp,
        price: selectedCab.price,
        discount: selectedCab.discount,
        CouponId: selectedCoupon?.id || null,
      };

      try {
        const response = await axios.post(
          "https://admin.travelsdata.com/api/Cab/Book",
          payload
        );

        console.log("Booking success:", response.data);

        const orderNo = response?.data?.data?.data?.booking?.order_no || "";

        console.log("Order Number:", orderNo);
        setPaymentInitializing(false);
        localStorage.setItem("order_no", orderNo);

        navigate("/cabgetdetails");
      } catch (error) {
        console.error("Booking failed:", error);
        setPaymentInitializing(false);
      }
      // setLoading(false); // stop loader
    }
  };

  // useEffect(() => {
  //   const cab = JSON.parse(localStorage.getItem("selectedCab"));
  //   setSelectedCab(cab);

  //   const selectedData = JSON.parse(localStorage.getItem("cabSearchSelected"));
  //   if (selectedData) {
  //     const { pickup, drop, start_date, end_date, start_time, end_time } = selectedData;

  //     // Optional: Set to state if you need to display it in the component
  //     setCabDetails({
  //       pickup,
  //       drop,
  //       start_date,
  //       end_date,
  //       start_time,
  //       end_time,
  //     });
  //   }
  // }, []);

  const [openBusReview, setOpenBusReview] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const handleSubmitReview = (e) => {
    e.preventDefault();

    if (validateForm()) {
      setOpenBusReview(true); // only open modal now
    } else {
      console.log("Validation failed", errors);
    }
    setConfirmLoading(false); // hide loader
  };

  useEffect(() => {
    const cab = JSON.parse(localStorage.getItem("selectedCab"));
    setSelectedCab(cab);

    console.log("selected cab data:", cab);

    const selectedData = JSON.parse(localStorage.getItem("cabSearchSelected"));
    if (selectedData) {
      const {
        pickup,
        drop,
        start_date,
        end_date,
        start_time,
        end_time,
        trip_type,
      } = selectedData;

      setCabDetails({
        pickup,
        drop,
        start_date,
        end_date,
        start_time,
        end_time,
      });

      setTripType(trip_type || "One Way Trip"); // default fallback
      setStartDate(start_date || "");
      setStartTime(start_time || "");
      setEndDate(end_date || "");
      setEndTime(end_time || "");
    }
  }, []);
  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [loadingCoupons, setLoadingCoupons] = useState(true);
  const [errorCoupons, setErrorCoupons] = useState(null);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const payload = { type: "cab" };
        const response = await axios.post(
          "https://admin.travelsdata.com/api/User/CouponCode",
          payload
        );

        if (response.data?.success) {
          setCoupons(response.data.data || []);
        } else {
          setErrorCoupons("Failed to fetch coupons");
        }
      } catch (err) {
        console.error("Error fetching coupons:", err);
        setErrorCoupons("Error fetching data");
      } finally {
        setLoadingCoupons(false);
      }
    };

    fetchCoupons();
  }, []);

  return (
    <>
      {selectedCab && (
        <div className="cabDetailsMain" style={{ position: "relative" }}>
          <div
            className="cabFinalBooking_MainBg"
            style={{ height: "150px" }}
          ></div>
          <div className="cabFinalBooking_Main">
            <div className="abFinalBooking_Containers">
              <Container>
                <Row>
                  <Col md={9}>
                    <div className="cabFinalBooking_ReviewBooking">
                      <div className="cabFinalBooking_reviewBook">
                        <Link to="/cablisting">
                          <span style={{ color: "white" }}>
                            <FaChevronLeft />
                          </span>
                        </Link>
                        <h3>Review Your Booking</h3>
                      </div>
                      {/* <div className="cabFinalBooking_reviewBook">
                    <span>
                      <BsInfoCircleFill />
                    </span>
                    <h4>Fare Rules</h4>
                  </div> */}
                    </div>

                    <div className="cabFinalBooking_cabDetails">
                      <div className="cabDetails-bookingCard">
                        <div className="cabDetails-bookingTop">
                          <div className="cabDetails-carSection">
                            <img
                              src="/Images/cab/img1.jpg"
                              alt="Sedan"
                              className="cabDetails-carImage"
                            />
                            {/* <div className="cabDetails-carType">Sedan</div> */}
                          </div>

                          <div className="cabDetails-routeInfo">
                            <p className="cabDetails-pickupDate">
                              Pickup :{" "}
                              {moment(cabDetails.start_date).format(
                                "D MMMM, YYYY"
                              )}
                              , {cabDetails.start_time}
                            </p>
                            <div className="cabDetails-route">
                              <div>
                                <strong>Pickup</strong>
                                <p>{cabDetails.pickup}</p>
                              </div>
                              <span className="cabDetails-arrow">→</span>
                              <div>
                                <strong>Drop-Off</strong>
                                <p>{cabDetails.drop}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="cabDetails-features">
                          <div className="cabDetails-featuresIcon">
                            <div className="cabDetails-featuresIconType">
                              <FaCarSide />
                            </div>
                            <div className="cabDetails-featuresType">
                              <div>Car Model</div>
                              <div className="cabDetails-link">
                                {selectedCab.name}
                              </div>
                            </div>
                          </div>
                          <div className="cabDetails-featuresIcon">
                            <div className="cabDetails-featuresIconType">
                              <SlSpeedometer />
                            </div>
                            <div className="cabDetails-featuresType">
                              <div>Km Charges</div>
                              <div className="cabDetails-link">
                                Rs. {selectedCab.extra_per_km}/km after{" "}
                                {selectedCab.distance} KM.
                              </div>
                            </div>
                          </div>
                          <div className="cabDetails-featuresIcon">
                            <div className="cabDetails-featuresIconType">
                              <BsFillFuelPumpFill />
                            </div>
                            <div className="cabDetails-featuresType">
                              <div>Fuel Type</div>
                              <div className="cabDetails-link">
                                ANY(CNG/Petrol/Diesel)
                              </div>
                            </div>
                          </div>
                          <div className="cabDetails-featuresIcon">
                            <div className="cabDetails-featuresIconType">
                              <FiPlusCircle />
                            </div>
                            <div className="cabDetails-featuresType">
                              <div>Extra</div>
                              <div className="cabDetails-link">
                                {selectedCab.total_seats} Seats
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="cabDetails-card">
                        <h4 className="cabDetails-heading">
                          Travellers Details
                        </h4>

                        <div className="cabDetails-titleBtns">
                          {["Mr.", "Ms.", "Mrs."].map((t) => (
                            <button
                              key={t}
                              className={`cabDetails-titleBtn ${
                                title === t ? "active" : ""
                              }`}
                              onClick={() => setTitle(t)}
                            >
                              {t}
                            </button>
                          ))}
                        </div>

                        <div className="cabDetails-formRow">
                          <div className="cabDetails-inputGroup">
                            <label>First Name</label>
                            <input
                              type="text"
                              placeholder="Enter First Name"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                            />
                            {errors.firstName && (
                              <span className="error-text">
                                {errors.firstName}
                              </span>
                            )}
                          </div>

                          <div className="cabDetails-inputGroup">
                            <label>Last Name</label>
                            <input
                              type="text"
                              placeholder="Enter Last Name"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                            />
                            {errors.lastName && (
                              <span className="error-text">
                                {errors.lastName}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="cabDetails-formRow">
                          <div className="cabDetails-inputGroup">
                            <label>Email</label>
                            <input
                              type="email"
                              placeholder="Enter Your Email Address"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                            />
                            {errors.email && (
                              <span className="error-text">{errors.email}</span>
                            )}
                          </div>
                          <div className="cabDetails-inputGroup">
                            <label>Phone</label>
                            <div className="cabDetails-phoneWrapper">
                              <select>
                                <option value="+91">+91</option>
                              </select>
                              <input
                                type="tel"
                                placeholder="Enter Mobile No"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                              />
                              {errors.phone && (
                                <span className="error-text">
                                  {errors.phone}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <p className="cabDetails-note">
                          Your booking details will be sent to this email
                          address and mobile number.
                        </p>

                        {/* GST Section */}
                        <div className="cabDetails-gstSection">
                          <label className="cabDetails-checkboxContainer">
                            <input
                              type="checkbox"
                              checked={showGST}
                              onChange={() => setShowGST(!showGST)}
                            />
                            <span className="cabDetails-checkboxLabel">
                              <span className="cabDetails-boldFont">
                                I have a GST number{" "}
                                <span style={{ color: "#9b9b9b" }}>
                                  (Optional)
                                </span>
                              </span>
                            </span>
                          </label>

                          {showGST && (
                            <div className="cabDetails-gstFields">
                              <Row className="cabDetails-gstRow">
                                <Col sm={6}>
                                  <label className="cabDetails-label">
                                    Company Name
                                  </label>
                                  <input
                                    className="form-control cabDetails-input"
                                    type="text"
                                    placeholder="Company Name"
                                  />
                                </Col>
                                <Col sm={6}>
                                  <label className="cabDetails-label">
                                    GST No
                                  </label>
                                  <input
                                    className="form-control cabDetails-input"
                                    type="text"
                                    placeholder="GST No"
                                  />
                                </Col>
                              </Row>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="cabDetails-tripDetailsWrapper">
                        <div className="cabDetails-tripDetailsCard">
                          <h4 className="cabDetails-title">Trip Details</h4>

                          {/* Pick-Up Address */}
                          <div className="cabDetails-fieldGroup">
                            <label className="cabDetails-Inputlabel">
                              Pick-Up Address{" "}
                              <span className="cabDetails-infoIcon">ℹ️</span>
                            </label>
                            <div className="cabDetails-inputWrapper">
                              <input
                                type="text"
                                className="cabDetails-input"
                                value={pickupAddress}
                                onChange={(e) =>
                                  setPickupAddress(e.target.value)
                                }
                              />
                              <div className="cabDetails-highlightText">
                                Indira Gandhi International Airport, Terminal 3
                              </div>
                              {errors.pickupAddress && (
                                <span className="error-text">
                                  {errors.pickupAddress}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Drop-Off Address */}
                          <div className="cabDetails-fieldGroup">
                            <label className="cabDetails-Inputlabel">
                              Drop-Off Address{" "}
                              <span className="cabDetails-infoIcon">ℹ️</span>
                            </label>
                            <div className="cabDetails-inputWrapper">
                              <input
                                type="text"
                                className="cabDetails-input"
                                placeholder="Enter Exact Drop Location"
                                value={dropAddress}
                                onChange={(e) => setDropAddress(e.target.value)}
                              />
                              <div className="cabDetails-highlightText">
                                GREATER KAILASH, SOUTH DELHI
                              </div>
                              {errors.dropAddress && (
                                <span className="error-text">
                                  {errors.dropAddress}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Choose Arrival */}
                          {/* <div className="cabDetails-arrivalSection">
                        <label className="cabDetails-Inputlabel">
                          Choose Arrival
                        </label>
                        <div className="cabDetails-radioGroup">
                          <label className="cabDetails-radioLabel">
                            <input
                              type="radio"
                              name="arrival"
                              className="cabDetails-radioInput"
                              checked
                              readOnly
                            />
                            Flight
                          </label>
                        </div>
                        <div className="cabDetails-inputWrapper">
                          <input
                            type="text"
                            className="cabDetails-input"
                            placeholder="Enter Flight No."
                          />
                        </div>
                      </div> */}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="cabFinalBooking_priceSummary">
                      <div className="cabFinalBooking_fareSummary">
                        <div>Fare Summary</div>
                        {/* <div>1 Cab</div> */}
                      </div>

                      <div className="cabFinalBooking_BasefareSummary">
                        <div>Base Fare -</div>
                        <div>₹ {selectedCab.price}</div>
                      </div>
                      {selectedCoupon?.discount > 0 && (
                        <div className="cabFinalBooking_BasefareSummary">
                          <div>Discount -</div>
                          <div>₹ {Math.round(selectedCoupon.discount)}</div>
                        </div>
                      )}

                      {/* <div className="cabFinalBooking_BasefareSummary">
                    <div>Taxes & Surcharges -</div>
                    <div>₹ 200</div>
                  </div>

                  <div className="cabFinalBooking_BasefareSummary">
                    <div>Discount -</div>
                    <div>₹ 100</div>
                  </div> */}

                      <div className="cabFinalBooking_PayAmt">
                        <div>You Pay: -</div>
                        <div>
                          ₹{" "}
                          {selectedCab.price - (selectedCoupon?.discount || 0)}
                        </div>
                      </div>

                      <button
                        type="button"
                        className="cabFinalBooking_continueBtn"
                        onClick={handleSubmitReview}
                      >
                        Continue
                      </button>

                      {openBusReview && (
                        <CabReviewPage
                          email={email}
                          phone={phone}
                          firstName={firstName}
                          lastName={lastName}
                          title={title}
                          onClose={() => setOpenBusReview(false)}
                          onConfirm={handlePayment} // call block+book here
                          confirmLoading={confirmLoading} // pass loading state
                        />
                      )}

                      <div>{/* <CabPromoCodes /> */}</div>
                    </div>

                    <PromoCode
                      availableCoupons={coupons}
                      onSelectCoupon={(coupon) => setSelectedCoupon(coupon)}
                    />
                  </Col>
                </Row>
                {paymentInitializing && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(0,0,0,0.45)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 9998,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: "16px 20px",
                        borderRadius: 8,
                        boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <span
                        className="spinner-border"
                        role="status"
                        aria-hidden="true"
                        style={{ width: 24, height: 24 }}
                      ></span>
                      <span style={{ fontWeight: 600 }}>
                        Initializing secure payment…
                      </span>
                    </div>
                  </div>
                )}

                {bookingProcessing && (
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      background: "rgba(0,0,0,0.45)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 9999,
                    }}
                  >
                    <div
                      style={{
                        background: "#fff",
                        padding: "24px 32px",
                        borderRadius: 12,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 16,
                        maxWidth: "400px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          border: "4px solid #f3f3f3",
                          borderTop: "4px solid #1d489f",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      ></div>
                      <div>
                        <h4
                          style={{
                            margin: "0 0 8px 0",
                            color: "#333",
                            fontSize: "18px",
                          }}
                        >
                          Please wait...
                        </h4>
                        <p
                          style={{
                            margin: 0,
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          Your booking is almost done! We're processing your
                          reservation.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </Container>
            </div>
          </div>
        </div>
      )}
      {selectedCab && (
        <div className="cabDetailsMobileMain">
          <div className="cabDetailsMobileMain-container">
            <div className="cabDetailsMobileMain-header">
              <div className="cabDetailsMobileMain-headerCabReview">
                <Link to="/cablisting">
                  <span style={{ fontSize: "25px" }}>
                    <IoMdArrowBack />
                  </span>
                </Link>
                <span>Cab Review & Traveler</span>
              </div>
            </div>

            <Container>
              <div className="cabDetailsMobile-MainContainer">
                <div className="cabDetailsMobileMain-card">
                  <h6 className="cabDetailsMobileMain-route">
                    {cabDetails.pickup} To {cabDetails.drop}
                  </h6>
                  <p className="cabDetailsMobileMain-pickup">
                    Pickup :{" "}
                    {moment(cabDetails.start_date).format("D MMMM, YYYY")},{" "}
                    {cabDetails.start_time}
                  </p>

                  <hr />

                  <div className="cabDetailsMobileMain-carDetails">
                    <img
                      src="/Images/cab/img1.jpg"
                      alt="Car"
                      className="cabDetailsMobileMain-carImage"
                    />
                    <div className="cabDetailsMobileMain-carInfo">
                      <p className="cabDetailsMobileMain-carTitle">
                        {selectedCab.name}
                      </p>
                      <p className="cabDetailsMobileMain-carMeta">
                        {selectedCab.total_seats} Seat | {selectedCab.distance}{" "}
                        Km
                      </p>
                    </div>
                  </div>

                  <hr />

                  <ul className="cabDetailsMobileMain-bulletPoints">
                    {selectedCab?.description &&
                      Array.from(
                        new DOMParser()
                          .parseFromString(
                            DOMPurify.sanitize(selectedCab.description),
                            "text/html"
                          )
                          .querySelectorAll("li")
                      ).map((li, index) => (
                        <li key={index}>{li.textContent.trim()}</li>
                      ))}
                  </ul>
                </div>

                <div className="cabDetailsMobileMain-card">
                  <h6 className="cabDetailsMobileMain-sectionTitle">
                    Traveller Details
                  </h6>
                  <p className="cabDetailsMobileMain-sectionSub">
                    Enter your details as per your Govt ID proof.
                  </p>

                  <div className="cabDetailsMobileMain-form">
                    <div className="cabDetailsMobileMain-row">
                      <div className="cabDetailsMobileMain-inputGroup">
                        <label>Title</label>
                        <select
                          className="cabDetailsMobileMain-select"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                        >
                          <option value="Mr.">Mr.</option>
                          <option value="Ms.">Ms.</option>
                          <option value="Mrs.">Mrs.</option>
                        </select>
                        {errors.title && (
                          <span className="error-text">{errors.title}</span>
                        )}
                      </div>

                      <div className="cabDetailsMobileMain-inputGroup">
                        <label>First Name</label>
                        <input
                          type="text"
                          placeholder="First Name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                        {errors.firstName && (
                          <span className="error-text">{errors.firstName}</span>
                        )}
                      </div>
                    </div>

                    <div className="cabDetailsMobileMain-inputGroup">
                      <label>Last Name</label>
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                      {errors.lastName && (
                        <span className="error-text">{errors.lastName}</span>
                      )}
                    </div>

                    <div
                      className="cabDetailsMobileMain-sectionTitle"
                      style={{ marginTop: "16px" }}
                    >
                      Contact Information
                    </div>

                    <div className="cabDetailsMobileMain-inputGroup cabDetailsMobileMain-inputWithIcon">
                      <label>Email Address</label>
                      <div className="cabDetailsMobileMain-inputWrapper">
                        <input
                          type="email"
                          placeholder="Enter Email Address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                        {errors.email && (
                          <span className="error-text">{errors.email}</span>
                        )}
                        <span className="cabDetailsMobileMain-icon">
                          <MdEmail />
                        </span>
                      </div>
                    </div>

                    <div className="cabDetailsMobileMain-inputGroup cabDetailsMobileMain-phoneRow">
                      <div className="cabDetailsMobileMain-phonePrefix">
                        +91
                      </div>
                      <div className="cabDetailsMobileMain-inputWrapper">
                        <input
                          type="tel"
                          placeholder="Enter Phone Number"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                        {errors.phone && (
                          <span className="error-text">{errors.phone}</span>
                        )}
                        <span className="cabDetailsMobileMain-icon">
                          <FaPhone />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cabCheckoutMainContainer">
                  <div className="tripDetailsCard">
                    <h4>Trip Details</h4>
                    <hr />
                    <div className="inputGroup">
                      <label>Pick-Up Address</label>
                      <input
                        type="text"
                        placeholder="Enter Pickup Address"
                        value={pickupAddress}
                        onChange={(e) => setPickupAddress(e.target.value)}
                      />
                      <div className="filledValue">NEW DELHI</div>
                      {errors.pickupAddress && (
                        <span className="error-text">
                          {errors.pickupAddress}
                        </span>
                      )}
                    </div>
                    <div className="inputGroup">
                      <label>Drop-Off Address</label>
                      <input
                        type="text"
                        placeholder="Enter Dropping Address"
                        value={dropAddress}
                        onChange={(e) => setDropAddress(e.target.value)}
                      />
                      <div className="filledValue">
                        Noida, Uttar Pradesh, India
                      </div>
                      {errors.dropAddress && (
                        <span className="error-text">{errors.dropAddress}</span>
                      )}
                    </div>
                  </div>

                  {/* <div className="tripDetailsCard">
                    <h4>Choose Payment Option</h4>
                    <div className="paymentOption selected">
                      <div className="radioCircle"></div>
                      <div className="paymentText">
                        <div className="paymentTitle">
                          Make part payment now
                        </div>
                        <div className="paymentSub">
                          Pay the rest to the driver
                        </div>
                      </div>
                      <div className="amount">₹220</div>
                    </div>

                    <div className="paymentOption">
                      <div className="radioCircle"></div>
                      <div className="paymentText">
                        <div className="paymentTitle">
                          Make full payment now
                        </div>
                      </div>
                      <div className="amount">₹879</div>
                    </div>
                  </div> */}

                  {/* GST Section */}
                  <div className="tripDetailsCard tripDetailsCard-GST ">
                    <div className="cabDetails-gstSection">
                      <label className="cabDetails-checkboxContainer">
                        <input
                          type="checkbox"
                          checked={showGST}
                          onChange={() => setShowGST(!showGST)}
                        />
                        <span className="cabDetails-checkboxLabel">
                          <span className="cabDetails-boldFont">
                            I have a GST number{" "}
                            <span style={{ color: "#9b9b9b" }}>(Optional)</span>
                          </span>
                        </span>
                      </label>

                      {showGST && (
                        <div className="cabDetails-gstFields">
                          <Row className="cabDetails-gstRow">
                            <Col sm={6}>
                              <label className="cabDetails-label">
                                Company Name
                              </label>
                              <input
                                className="form-control cabDetails-input"
                                type="text"
                                placeholder="Company Name"
                              />
                            </Col>
                            <Col sm={6}>
                              <label className="cabDetails-label">GST No</label>
                              <input
                                className="form-control cabDetails-input"
                                type="text"
                                placeholder="GST No"
                              />
                            </Col>
                          </Row>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="review_cabbb">
                    {openBusReview && (
                      <CabReviewPage
                        email={email}
                        phone={phone}
                        firstName={firstName}
                        lastName={lastName}
                        title={title}
                        onClose={() => setOpenBusReview(false)}
                        onConfirm={handlePayment} // call block+book here
                        confirmLoading={confirmLoading} // pass loading state
                      />
                    )}
                  </div>
                  <PromoCode
                    availableCoupons={coupons}
                    onSelectCoupon={(coupon) => setSelectedCoupon(coupon)}
                  />

                  <div className="stickyFooter">
                    <div className="footerLeft">
                      <div className="footerLabel">Grand Total</div>
                      <div className="footerAmount">
                        ₹{selectedCab.price - selectedCoupon?.discount || 0}
                      </div>
                    </div>
                    <button
                      onClick={handleSubmitReview}
                      className="cab-ContinueBtn"
                    >
                      Continue{" "}
                    </button>
                  </div>
                </div>
              </div>
              {paymentInitializing && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9998,
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      padding: "16px 20px",
                      borderRadius: 8,
                      boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <span
                      className="spinner-border"
                      role="status"
                      aria-hidden="true"
                      style={{ width: 24, height: 24 }}
                    ></span>
                    <span style={{ fontWeight: 600 }}>
                      Initializing secure payment…
                    </span>
                  </div>
                </div>
              )}

              {bookingProcessing && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(0,0,0,0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      padding: "24px 32px",
                      borderRadius: 12,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 16,
                      maxWidth: "400px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        border: "4px solid #f3f3f3",
                        borderTop: "4px solid #1d489f",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    ></div>
                    <div>
                      <h4
                        style={{
                          margin: "0 0 8px 0",
                          color: "#333",
                          fontSize: "18px",
                        }}
                      >
                        Please wait...
                      </h4>
                      <p
                        style={{
                          margin: 0,
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        Your booking is almost done! We're processing your
                        reservation.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </Container>

            {/* <div className="cabDetailsMobileMain-footer">
    <div className="cabDetailsMobileMain-price">
      <span>Grand Total</span>
      <strong>₹2692</strong>
      <span className="cabDetailsMobileMain-infoIcon">i</span>
    </div>
    <button className="cabDetailsMobileMain-btn">Continue Booking</button>
  </div> */}
          </div>
        </div>
      )}
    </>
  );
};

export default CabDetails;
